import { FC, useMemo } from "react"
import {
  Box,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material"
import { ChevronLeft, ChevronRight, Refresh } from "@mui/icons-material"
import { DatePicker } from "@mui/x-date-pickers"
import { PERIOD_PRESETS, Period, formatPeriodLabel } from "../model/period"
import { civilFromEpochDay } from "../model/mskCalendar"

interface PeriodToolbarProps {
  period: Period
  /** Очередь коммитов не пуста — «Обновить» заблокирована. */
  isBusy: boolean
  onSetWeeks: (weeks: number) => void
  onShift: (deltaWeeks: number) => void
  onCurrentWeek: () => void
  onSetStart: (date: Date) => void
  onRefresh: () => void
}

/**
 * LocalizationProvider (AdapterDateFns, ru) уже смонтирован в App.tsx:507 — DatePicker
 * используется напрямую. Дата для пикера конструируется как ЛОКАЛЬНАЯ (new Date(y, m-1, d)):
 * пользователь выбирает день в календаре, его wall-clock и есть намерение (спека, раздел 4).
 */
export const PeriodToolbar: FC<PeriodToolbarProps> = ({
  period,
  isBusy,
  onSetWeeks,
  onShift,
  onCurrentWeek,
  onSetStart,
  onRefresh,
}) => {
  const startDate = useMemo(() => {
    const { year, month, day } = civilFromEpochDay(period.startEpochDay)
    return new Date(year, month - 1, day)
  }, [period.startEpochDay])

  return (
    <Box
      sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
    >
      <ToggleButtonGroup
        size="small"
        exclusive
        value={period.weeks}
        onChange={(_, v) => v !== null && onSetWeeks(v)}
      >
        {PERIOD_PRESETS.map((w) => (
          <ToggleButton key={w} value={w}>
            {w} нед.
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <IconButton
        size="small"
        onClick={() => onShift(-1)}
        aria-label="Неделей раньше"
      >
        <ChevronLeft />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => onShift(1)}
        aria-label="Неделей позже"
      >
        <ChevronRight />
      </IconButton>
      <Button size="small" variant="outlined" onClick={onCurrentWeek}>
        Текущая неделя
      </Button>
      <DatePicker
        label="Начало периода"
        value={startDate}
        onChange={(date) => date && onSetStart(date)}
        slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
      />
      <Box sx={{ flex: 1 }} />
      <Box sx={{ fontSize: 14, color: "text.secondary" }}>
        {formatPeriodLabel(period)}
      </Box>
      <Tooltip
        title={
          isBusy ? "Дождитесь применения изменений" : "Перезапросить расписание"
        }
      >
        {/* span обязателен: disabled-кнопка не отдаёт события, и Tooltip без обёртки молчит */}
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Refresh />}
            disabled={isBusy}
            onClick={onRefresh}
          >
            Обновить
          </Button>
        </span>
      </Tooltip>
    </Box>
  )
}
